'use client';

import React, { useState } from 'react';
import { Bell, Search, ChevronDown, AlertTriangle, Menu } from 'lucide-react';

const notifications = [
  { id: 'notif-001', type: 'alert', message: 'License expiring: Sarah Chen (RN) — 6 days', time: '2m ago' },
  { id: 'notif-002', type: 'warning', message: '7 open shifts unfilled for tonight', time: '18m ago' },
  { id: 'notif-003', type: 'info', message: 'Invoice #INV-2847 marked paid by Riverside Medical', time: '1h ago' },
  { id: 'notif-004', type: 'alert', message: 'Marcus Webb clocked out 2.5h early — approval needed', time: '2h ago' },
  { id: 'notif-005', type: 'info', message: '3 new applicants for Travel RN posting', time: '3h ago' },
];

const roles = ['Admin', 'Manager', 'Staff'];

interface TopbarProps {
  sidebarCollapsed: boolean;
  onMobileMenuToggle: () => void;
}

export default function Topbar({ sidebarCollapsed, onMobileMenuToggle }: TopbarProps) {
  const [showNotifs, setShowNotifs] = useState(false);
  const [activeRole, setActiveRole] = useState('Admin');
  const [showRoleMenu, setShowRoleMenu] = useState(false);

  const unreadCount = notifications.length;

  return (
    <header
      className={`fixed top-0 right-0 h-[60px] bg-card border-b border-border z-30 flex items-center justify-between px-4 sm:px-5 content-transition left-0 ${
        sidebarCollapsed ? 'md:left-16' : 'md:left-60'
      }`}
    >
      {/* Left: Hamburger (mobile) + Page context */}
      <div className="flex items-center gap-3">
        {/* Hamburger button — mobile only */}
        <button
          onClick={onMobileMenuToggle}
          className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors md:hidden flex-shrink-0"
          aria-label="Open navigation menu"
        >
          <Menu size={20} />
        </button>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-500 hidden sm:inline">MedStaff</span>
          <span className="hidden sm:inline">/</span>
          <span className="text-foreground font-500">Operations</span>
        </div>
      </div>

      {/* Center: Search */}
      <div className="flex-1 max-w-xs mx-3 sm:mx-6">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search..."
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-muted rounded-lg border border-transparent focus:border-primary focus:bg-card focus:outline-none transition-all"
          />
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground bg-border rounded px-1 py-0.5 hidden sm:block">
            ⌘K
          </span>
        </div>
      </div>

      {/* Right: Role switcher + notifications */}
      <div className="flex items-center gap-2 sm:gap-3">
        {/* Role switcher — hidden on small mobile */}
        <div className="relative hidden sm:block">
          <button
            onClick={() => setShowRoleMenu(!showRoleMenu)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-muted text-sm font-500 text-foreground hover:bg-secondary transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
            {activeRole}
            <ChevronDown size={13} />
          </button>
          {showRoleMenu && (
            <div className="absolute right-0 top-full mt-1 w-36 bg-card border border-border rounded-lg shadow-elevated z-50 py-1 scale-in">
              {roles.map((role) => (
                <button
                  key={`role-${role}`}
                  onClick={() => { setActiveRole(role); setShowRoleMenu(false); }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors ${
                    activeRole === role ? 'text-primary font-600' : 'text-foreground'
                  }`}
                >
                  {role}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifs(!showNotifs)}
            className="relative p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            aria-label={`${unreadCount} notifications`}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-danger text-white text-[9px] font-700 rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {showNotifs && (
            <div className="absolute right-0 top-full mt-2 w-72 sm:w-80 bg-card border border-border rounded-xl shadow-modal z-50 scale-in">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <span className="font-600 text-sm">Notifications</span>
                <span className="text-xs text-primary font-500 cursor-pointer hover:underline">Mark all read</span>
              </div>
              <ul className="divide-y divide-border max-h-72 overflow-y-auto scrollbar-thin">
                {notifications.map((n) => (
                  <li key={n.id} className="flex items-start gap-3 px-4 py-3 hover:bg-muted transition-colors cursor-pointer">
                    <div className={`mt-0.5 flex-shrink-0 ${n.type === 'alert' ? 'text-danger' : n.type === 'warning' ? 'text-warning' : 'text-primary'}`}>
                      <AlertTriangle size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-foreground leading-snug">{n.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{n.time}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="px-4 py-2.5 border-t border-border">
                <span className="text-xs text-primary font-500 cursor-pointer hover:underline">View all notifications</span>
              </div>
            </div>
          )}
        </div>

        {/* Last updated */}
        <span className="text-[11px] text-muted-foreground hidden lg:block">
          Updated 2m ago
        </span>
      </div>
    </header>
  );
}